import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    Tabs,
    Space,
    Button,
    Table,
    Card,
    Statistic,
    Row,
    Col,
    notification,
    Tag,
    Typography,
    Divider,
    Spin
} from 'antd';

import {
    UserOutlined,
    BarChartOutlined,
    DownloadOutlined,
    TeamOutlined,
    BankOutlined
} from '@ant-design/icons';

import { DiventryMenu } from '../../../Templates/Menu';
import {
    DiventryLayoutThreatDB,
    DiventryLayoutHeader,
    DiventryLayoutContent,
    DiventryLayoutFooter,
    DiventryBlock
} from '../../../Templates/Layout';

import Highcharts from 'highcharts';
import HC_exporting from 'highcharts/modules/exporting';
import HighchartsReact from 'highcharts-react-official'
import highcharts3d from "highcharts/highcharts-3d";
HC_exporting(Highcharts);
highcharts3d(Highcharts);

const { Title, Text } = Typography;

function PersonaList({ kernel }) {
    const [personas, setPersonas] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();

    useEffect(() => {
        fetchPersonas();
    }, []);

    const fetchPersonas = async () => {
        setLoading(true);
        try {
            const response = await kernel.api.get("/dohzel/persona/list");
            if (response.error) {
                notification.error({
                    message: t("Error.fetchingInformation"),
                    description: response.error
                });
                return;
            }
            setPersonas(response.data || []);
        } catch (error) {
            notification.error({
                message: t("Error.fetchingInformation"),
                description: error.message
            });
        } finally {
            setLoading(false);
        }
    };

    const exportPersonas = async () => {
        try {
            // Utiliser window.open directement avec l'URL de l'API
            window.open(kernel.config.central + '/dohzel/persona/export', '_blank');
            
            notification.info({
                message: t("Persona.exportingData"),
                description: t("Persona.exportingDataDescription")
            });
        } catch (error) {
            notification.error({
                message: t("Error.exportingData"),
                description: error.message
            });
        }
    };

    const getPersonaTypeIcon = (type) => {
        if (type === "b2c") return <UserOutlined />;
        if (type === "b2b") return <BankOutlined />;
        return <TeamOutlined />;
    };

    const getCyberSecurityLevelColor = (level) => {
        switch (level) {
            case "beginner": return "green";
            case "intermediate": return "blue";
            case "advanced": return "orange";
            case "expert": return "red";
            default: return "default";
        }
    };

    const columns = [
        {
            title: t("Persona.type"),
            dataIndex: "personaType",
            key: "personaType",
            render: (type) => (
                <Tag icon={getPersonaTypeIcon(type)}>
                    {type === "b2c" ? "B2C" : "B2B"}
                </Tag>
            ),
            filters: [
                { text: "B2C", value: "b2c" },
                { text: "B2B", value: "b2b" }
            ],
            onFilter: (value, record) => record.personaType === value
        },
        {
            title: t("Persona.email"),
            dataIndex: "email",
            key: "email",
            render: (email) => email || "-"
        },
        {
            title: t("Persona.cyberSecurityLevel"),
            dataIndex: "cyberSecurityLevel",
            key: "cyberSecurityLevel",
            render: (level) => (
                <Tag color={getCyberSecurityLevelColor(level)}>
                    {level}
                </Tag>
            ),
            filters: [
                { text: "Beginner", value: "beginner" },
                { text: "Intermediate", value: "intermediate" },
                { text: "Advanced", value: "advanced" },
                { text: "Expert", value: "expert" }
            ],
            onFilter: (value, record) => record.cyberSecurityLevel === value
        },
        {
            title: t("Persona.appIdentifier"),
            dataIndex: "appIdentifier",
            key: "appIdentifier",
            ellipsis: true
        },
        {
            title: t("Persona.createdAt"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => new Date(date).toLocaleString(),
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            defaultSortOrder: "descend"
        }
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16, float: "right" }}>
                <Button 
                    type="primary" 
                    icon={<DownloadOutlined />} 
                    onClick={exportPersonas}
                >
                    {t("Persona.exportData")}
                </Button>
            </Space>
            <Table 
                dataSource={personas} 
                columns={columns} 
                loading={loading}
                rowKey={"id"}
                expandable={{
                    expandedRowRender: (record) => (
                        <div style={{ padding: "0 20px" }}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Card title={t("Persona.commonInfo")} size="small">
                                        <p><strong>{t("Persona.howHeard")}:</strong> {record.commonQuestions?.howHeard || "-"}</p>
                                        <p><strong>{t("Persona.expectations")}:</strong> {record.commonQuestions?.expectations || "-"}</p>
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    {record.personaType === "b2c" ? (
                                        <Card title={t("Persona.b2cInfo")} size="small">
                                            <p><strong>{t("Persona.usageFrequency")}:</strong> {record.b2c?.usageFrequency || "-"}</p>
                                            <p><strong>{t("Persona.privacyConcern")}:</strong> {record.b2c?.privacyConcern || "-"}</p>
                                            <p><strong>{t("Persona.deviceCount")}:</strong> {record.b2c?.deviceCount || "-"}</p>
                                        </Card>
                                    ) : (
                                        <Card title={t("Persona.b2bInfo")} size="small">
                                            <p><strong>{t("Persona.companySize")}:</strong> {record.b2b?.companySize || "-"}</p>
                                            <p><strong>{t("Persona.industry")}:</strong> {record.b2b?.industry || "-"}</p>
                                            <p><strong>{t("Persona.securityBudget")}:</strong> {record.b2b?.securityBudget || "-"}</p>
                                        </Card>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    )
                }}
            />
        </div>
    );
}

function PersonaStats({ kernel }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const [optionsPie, setOptionsPie] = useState({});
    const [optionsBar, setOptionsBar] = useState({});

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const response = await kernel.api.get("/dohzel/persona/stats");
            if (response.error) {
                notification.error({
                    message: t("Error.fetchingInformation"),
                    description: response.error
                });
                return;
            }
            setStats(response.data);
            
            // S'assurer que les données sont disponibles avant de configurer les graphiques
            if (response.data && response.data.byType && response.data.byCyberSecurityLevel) {
                setOptionsPie({
                    chart: {
                        type: 'pie',
                        options3d: {
                            enabled: true,
                            alpha: 45
                        }
                    },
                    title: {
                        text: t("Persona.distributionByType")
                    },
                    subtitle: {
                        text: '',
                        align: 'left'
                    },
                    plotOptions: {
                        pie: {
                            // innerSize: 100,
                            depth: 45
                        }
                    },
                    credits: {
                        enabled: false
                    },
                    series: [{
                        name: 'Persona',
                        colorByPoint: true,
                        // colors: ["#3498db", "#f1c40f"],
                        data: Object.entries(response.data.byType).map(([key, value]) => [key, value])
                    }],
                });

                setOptionsBar({
                    chart: {
                        type: 'column'
                    },
                    title: {
                        text: t('Persona.distributionByCyberSecurityLevel')
                    },
                    subtitle: {
                        text: null, //'Click the columns to view versions.'
                    },
                    accessibility: {
                        announceNewData: {
                            enabled: true
                        }
                    },
                    xAxis: {
                        type: 'category'
                    },
                    yAxis: {
                        title: {
                            text: 'Total'
                        }
                
                    },
                    legend: {
                        enabled: false
                    },
                    plotOptions: {
                        series: {
                            borderWidth: 0,
                            dataLabels: {
                                enabled: true,
                                format: '{point.y}'
                            }
                        }
                    },
                    credits: {
                        enabled: false
                    },
                
                    tooltip: {
                        headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
                        pointFormat: '<span style="color:{point.color}">{point.name}</span>: ' +
                            '<b>{point.y}</b> of total<br/>'
                    },
                
                    series: [
                        {
                            name: 'Persona',
                            colorByPoint: true,
                            colors: ["#1abc9c", "#3498db", "#e67e22", "#e74c3c"],
                            data: Object.entries(response?.data?.byCyberSecurityLevel)?.map(([key, value]) => ({
                                name: key,
                                y: value || 0
                            }))
                        }
                    ]
                });
            }
        } catch (error) {
            notification.error({
                message: t("Error.fetchingInformation"),
                description: error.message
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading || !stats) {
        return <div>{t("Common.loading")}...</div>;
    }

    return (
        <div>
            <Title level={4}>{t("Persona.distributionByType")}</Title>
            <Row gutter={16}>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title={t("Persona.totalPersonas")}
                            value={stats.total}
                            prefix={<TeamOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="B2C"
                            value={stats?.byType?.b2c}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic
                            title="B2B"
                            value={stats?.byType?.b2b}
                            prefix={<BankOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Divider />

            <Title level={4}>{t("Persona.distributionByCyberSecurityLevel")}</Title>
            <Row gutter={16}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t("Persona.beginner")}
                            value={stats?.byCyberSecurityLevel?.beginner}
                            valueStyle={{ color: '#1abc9c' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t("Persona.intermediate")}
                            value={stats?.byCyberSecurityLevel?.intermediate}
                            valueStyle={{ color: '#3498db' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t("Persona.advanced")}
                            value={stats?.byCyberSecurityLevel?.advanced}
                            valueStyle={{ color: '#e67e22' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title={t("Persona.expert")}
                            value={stats?.byCyberSecurityLevel?.expert}
                            valueStyle={{ color: '#e74c3c' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Card style={{ ...styles.statContainer, textAlign: 'left' }}>
                        <HighchartsReact highcharts={Highcharts} options={optionsBar} />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card style={{ ...styles.pieContainer, textAlign: 'left' }}>
                        <HighchartsReact highcharts={Highcharts} options={optionsPie} />
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export function Persona({ kernel }) {
    const navigate = useNavigate();
    const params = useParams();
    const { t } = useTranslation();

    const [tabs, setTabs] = useState([]);

    const pages = {
        'stats': {
            key: 'stats',
            title: "Persona.statistics",
            description: "Persona.statisticsDescription",
            icon: <BarChartOutlined />,
            ref: PersonaStats
        },
        'list': {
            key: 'list',
            title: "Persona.list",
            description: "Persona.listDescription",
            icon: <TeamOutlined />,
            ref: PersonaList
        },
    };

    useEffect(() => {
        setTabs(Object.values(pages));
    }, [params]);

    return (
        <DiventryLayoutThreatDB>
            <DiventryMenu kernel={kernel} />
            <DiventryLayoutHeader
                title={<Space align="center">
                    <TeamOutlined style={{ fontSize: '24px' }} />
                    <div>{t("Persona.title")}</div>
                </Space>}
                description={t("Persona.description")}
            />
            <DiventryLayoutContent>
                <DiventryBlock>
                    <Tabs
                        type="card"
                        activeKey={params.page1}
                        onChange={(tab) => {
                            navigate(`/admin/dohzel-persona/${tab}`);
                        }}
                        items={tabs.map((item) => {
                            return ({
                                key: item.key,
                                label: (
                                    <span>
                                        {item.icon} {t(item.title)}
                                    </span>
                                ),
                                children: <div style={{ paddingTop: 10 }}>
                                    <item.ref kernel={kernel} />
                                </div>
                            });
                        })}
                    />
                </DiventryBlock>
            </DiventryLayoutContent>
            <DiventryLayoutFooter kernel={kernel} />
        </DiventryLayoutThreatDB>
    );
}

const styles = {
    statContainer: {
        marginTop: 25,
        textAlign: 'center'
    },
    pieContainer: {
        marginTop: 25,
        textAlign: 'center'
    }
}